import React, { useState, useMemo } from "react";
import { 
  Box, 
  useTheme, 
  TextField, 
  InputAdornment, 
  MenuItem, 
  FormControl, 
  InputLabel, 
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  IconButton,
  Collapse,
  Typography,
  Tooltip
} from "@mui/material";
import SearchIcon from '@mui/icons-material/Search';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import CloseIcon from '@mui/icons-material/Close';
import { TABLE_HEAD_POSITIONS, TABLE_HEAD_LOT } from "../../component/Methods";
import { calculateAndFormatNoAbs, findCust, findStrategy, formatValueDecimal } from "../../component/Methods";
import moment from "moment";
import ClosePositionDialog from "./ClosePositionDialog";

const PositionsTable = ({ positions, originalPositions, funds, strategies, custodians }) => {
  const theme = useTheme();
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(1000);
  const [selectedFund, setSelectedFund] = useState("");
  const [selectedStrategy, setSelectedStrategy] = useState("");
  const [openRowIndex, setOpenRowIndex] = useState(null);
  const [sortField, setSortField] = useState("tickerSymbol");
  const [sortDirection, setSortDirection] = useState("asc");
  const [closePositionDialogOpen, setClosePositionDialogOpen] = useState(false);
  const [selectedPositionForClose, setSelectedPositionForClose] = useState(null);

  // Create sorted arrays for dropdowns
  const sortedFunds = useMemo(() => {
    if (!funds) return [];
    return [...funds].sort((a, b) => 
      (a.abbreviation || a.fundId || "").localeCompare(b.abbreviation || b.fundId || "")
    );
  }, [funds]);

  const sortedStrategies = useMemo(() => {
    if (!strategies) return [];
    return [...strategies].sort((a, b) => 
      (a.name || a.strategyId || "").localeCompare(b.name || b.strategyId || "")
    );
  }, [strategies]);

  // Sorting function
  const sortPositions = (positions) => {
    return [...positions].sort((a, b) => {
      let aValue = a[sortField];
      let bValue = b[sortField];
      
      // Handle special cases
      if (sortField === "tickerSymbol") {
        // Special sorting for tickers: letters first, then numbers
        const aStartsWithNumber = /^\d/.test((aValue || "").toLowerCase());
        const bStartsWithNumber = /^\d/.test((bValue || "").toLowerCase());
        
        if (aStartsWithNumber && !bStartsWithNumber) return 1;
        if (!aStartsWithNumber && bStartsWithNumber) return -1;
        
        aValue = (aValue || "").toLowerCase();
        bValue = (bValue || "").toLowerCase();
      } else if (sortField === "shares" || sortField === "averageCostPerShare" || sortField === "price") {
        // Numeric sorting
        aValue = parseFloat(aValue) || 0;
        bValue = parseFloat(bValue) || 0;
      } else {
        // String sorting
        aValue = (aValue || "").toLowerCase();
        bValue = (bValue || "").toLowerCase();
      }
      
      if (aValue < bValue) return sortDirection === "asc" ? -1 : 1;
      if (aValue > bValue) return sortDirection === "asc" ? 1 : -1;
      return 0;
    });
  };

  // Filter positions based on search and selections
  const filteredPositions = useMemo(() => {
    let filtered = originalPositions || [];
    
    if (selectedFund) {
      filtered = filtered.filter(item => item.fund === selectedFund);
    }
    
    if (selectedStrategy) {
      filtered = filtered.filter(item => item.strategy === selectedStrategy);
    }
    
    if (search) {
      const searchLower = search.toLowerCase();
      filtered = filtered.filter(item =>
        (item.tickerSymbol || "").toLowerCase().includes(searchLower) ||
        (item.fundName || "").toLowerCase().includes(searchLower) ||
        (item.strategyName || "").toLowerCase().includes(searchLower) ||
        (item.custodianName || "").toLowerCase().includes(searchLower)
      );
    }
    
    // Create a new array and sort by ticker symbol alphabetically
    const sortedFiltered = sortPositions(filtered);
    
    return sortedFiltered.map((position, index) => ({
      id: `${position.tickerSymbol}-${position.fundName}-${index}`,
      ...position,
    }));
  }, [originalPositions, selectedFund, selectedStrategy, search, sortField, sortDirection]);

  const toggleRow = (index) => {
    setOpenRowIndex(openRowIndex === index ? null : index);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      setSortField(field);
      setSortDirection("asc");
    }
    setPage(0); // Reset to first page when sorting
  };

  const handleClosePosition = (position) => {
    setSelectedPositionForClose(position);
    setClosePositionDialogOpen(true);
  };

  const handleClosePositionDialogClose = () => {
    setClosePositionDialogOpen(false);
    setSelectedPositionForClose(null);
  };

  return (
    <Box sx={{ width: "100%" }}>
      {/* Filter Controls */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          p: 2,
          borderBottom: `1px solid ${theme.palette.divider}`,
          gap: 2,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 2, flex: 1 }}>
          <TextField
            size="small"
            placeholder="Search positions..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ color: "text.disabled" }} />
                </InputAdornment>
              ),
            }}
            sx={{
              width: 300,
              "& .MuiOutlinedInput-root": {
                borderRadius: 1,
              },
            }}
          />
          
          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel>Fund</InputLabel>
            <Select
              value={selectedFund}
              label="Fund"
              onChange={(e) => setSelectedFund(e.target.value)}
              sx={{ borderRadius: 1 }}
            >
              <MenuItem value="">All Funds</MenuItem>
              {sortedFunds.map((fund) => (
                <MenuItem key={fund.fundId} value={fund.fundId}>
                  {fund.abbreviation || fund.fundId}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          
          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel>Strategy</InputLabel>
            <Select
              value={selectedStrategy}
              label="Strategy"
              onChange={(e) => setSelectedStrategy(e.target.value)}
              sx={{ borderRadius: 1 }}
            >
              <MenuItem value="">All Strategies</MenuItem>
              {sortedStrategies.map((strategy) => (
                <MenuItem key={strategy.strategyId} value={strategy.strategyId}>
                  {strategy.name || strategy.strategyId}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
        
        <Box sx={{ color: theme.palette.text.secondary, fontSize: '14px' }}>
          {filteredPositions.length} positions
        </Box>
      </Box>

      {/* Table */}
      <TableContainer sx={{ height: "calc(100vh - 80px)", overflowY: "scroll" }}>
        <Table stickyHeader size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={{ width: 50,backgroundColor: theme.palette.grey[100] }}></TableCell>
              {TABLE_HEAD_POSITIONS.map((headCell) => (
                <TableCell
                  key={headCell.id}
                  align={headCell.alignLeft ? "left" : "right"}
                  onClick={() => handleSort(headCell.id)}
                  sx={{
                    fontWeight: 600,
                    backgroundColor: theme.palette.grey[100],
                    color: theme.palette.text.primary,
                    borderBottom: `2px solid ${theme.palette.divider}`,
                    minWidth: headCell.width ? headCell.width : "auto",
                    maxWidth: 20,
                    cursor: "pointer",
                    userSelect: "none",
                    "&:hover": {
                      backgroundColor: theme.palette.grey[200],
                    },
                    position: "relative",
                  }}
                >
                  <Box sx={{ display: "flex", alignItems: "center", justifyContent: headCell.alignLeft ? "flex-start" : "flex-end" }}>
                    {headCell.label}
                    {sortField === headCell.id && (
                      <Box
                        component="span"
                        sx={{
                          ml: 0.5,
                          fontSize: "12px",
                          color: theme.palette.primary.main,
                          fontWeight: "bold",
                        }}
                      >
                        {sortDirection === "asc" ? "↑" : "↓"}
                      </Box>
                    )}
                  </Box>
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredPositions
              .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
              .map((row, index) => {
                const hasLots = Array.isArray(row?.lots) && row?.lots.length > 0;
                const isOpen = openRowIndex === index;

                return (
                  <React.Fragment key={row.id}>
                    <TableRow
                      sx={{
                        "&:hover": {
                          backgroundColor: theme.palette.action.hover,
                        },
                      }}
                    >
                      <TableCell sx={{ width: 50 }}>
                        {hasLots && (
                          <IconButton
                            size="small"
                            onClick={() => toggleRow(index)}
                            sx={{ height: "20px", width: "20px" }}
                          >
                            {isOpen ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
                          </IconButton>
                        )}
                      </TableCell>
                      <TableCell sx={{ fontWeight: 500, color: theme.palette.text.primary }}>
                        {row.positionType || "-"}
                      </TableCell>
                      <TableCell sx={{ fontWeight: 600, color: theme.palette.primary.main }}>
                        {row.tickerSymbol ? row.tickerSymbol.toUpperCase() : "-"}
                      </TableCell>
                      <TableCell sx={{ color: theme.palette.text.primary }}>
                        {row.fundName || "-"}
                      </TableCell>
                      <TableCell sx={{ color: theme.palette.text.primary }}>
                        {row.strategyName || "-"}
                      </TableCell>
                      <TableCell sx={{ color: theme.palette.text.primary }}>
                        {row.custodianName || "-"}
                      </TableCell>
                      <TableCell align="right" sx={{ 
                        fontWeight: 500, 
                        color: row.shares >= 0 ? theme.palette.success.main : theme.palette.error.main 
                      }}>
                        {row.shares ? (row.shares < 0 ? `(${Math.abs(row.shares).toLocaleString()})` : row.shares.toLocaleString()) : "-"}
                      </TableCell>
                      <TableCell align="right" sx={{ color: theme.palette.text.primary }}>
                        {row.averageCostPerShare ? `$${row.averageCostPerShare.toFixed(2)}` : "-"}
                      </TableCell>
                      <TableCell align="right" sx={{ fontWeight: 500, color: theme.palette.text.primary }}>
                        {row.price ? `$${row.price.toFixed(2)}` : "-"}
                      </TableCell>
                      <TableCell align="center">
                        <Tooltip title="Close this position" arrow>
                          <IconButton
                            size="small"
                            onClick={() => handleClosePosition(row)}
                            sx={{
                              color: theme.palette.error.main,
                              "&:hover": {
                                backgroundColor: theme.palette.error.light,
                                color: theme.palette.error.contrastText,
                              },
                            }}
                          >
                            <CloseIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>

                    {/* Expandable Lots Row */}
                    {hasLots && (
                      <TableRow>
                        <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={10}>
                          <Collapse in={isOpen} timeout="auto" unmountOnExit>
                            <Box sx={{ margin: 1 }}>
                              <Typography variant="h6" sx={{ mb: 2, color: theme.palette.text.secondary }}>
                                Lot Details
                              </Typography>
                              <Table size="small">
                                <TableHead>
                                  <TableRow>
                                    {TABLE_HEAD_LOT.map((headCell) => (
                                      <TableCell
                                        key={headCell.id}
                                        align={headCell.alignRight ? "right" : "left"}
                                        sx={{
                                          fontWeight: 600,
                                          backgroundColor: theme.palette.grey[50],
                                          color: theme.palette.text.secondary,
                                          borderBottom: `1px solid ${theme.palette.divider}`,
                                        }}
                                      >
                                        {headCell.label}
                                      </TableCell>
                                    ))}
                                  </TableRow>
                                </TableHead>
                                <TableBody>
                                  {row.lots.map((lot, lotIndex) => (
                                    <TableRow key={lotIndex} sx={{ "&:hover": { backgroundColor: theme.palette.grey[50] } }}>
                                      <TableCell sx={{ whiteSpace: "nowrap" }}>
                                        {lot.date ? moment(lot.date).format("MMM DD, YYYY") : "-"}
                                      </TableCell>
                                      <TableCell sx={{ whiteSpace: "nowrap" }}>
                                        {findStrategy(lot.strategy, strategies) || "-"}
                                      </TableCell>
                                      <TableCell sx={{ whiteSpace: "nowrap" }}>
                                        {findCust(lot.account?.custodian, custodians) || "-"}
                                      </TableCell>
                                      <TableCell align="right" sx={{ whiteSpace: "nowrap" }}>
                                        {lot.shareCount < 0
                                          ? `(${Math.abs(lot.shareCount).toLocaleString()})`
                                          : lot.shareCount.toLocaleString()}
                                      </TableCell>
                                      <TableCell align="right" sx={{ whiteSpace: "nowrap" }}>
                                        {lot.costPerShare < 0
                                          ? `(${formatValueDecimal(Math.abs(lot.costPerShare))})`
                                          : formatValueDecimal(lot.costPerShare)}
                                      </TableCell>
                                      <TableCell align="right" sx={{ whiteSpace: "nowrap" }}>
                                        {lot.sharePrice < 0
                                          ? `(${formatValueDecimal(Math.abs(lot.sharePrice))})`
                                          : formatValueDecimal(lot.sharePrice)}
                                      </TableCell>
                                      <TableCell align="right" sx={{ whiteSpace: "nowrap" }}>
                                        {lot.position === 0 ? "Long" : "Short"}
                                      </TableCell>
                                    </TableRow>
                                  ))}
                                </TableBody>
                              </Table>
                            </Box>
                          </Collapse>
                        </TableCell>
                      </TableRow>
                    )}
                  </React.Fragment>
                );
              })}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Pagination */}
       <TablePagination
        rowsPerPageOptions={[25, 50, 100, 500, 1000]}
        component="div"
        count={filteredPositions.length}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
        sx={{
          borderTop: `1px solid ${theme.palette.divider}`,
          backgroundColor: theme.palette.grey[100],
        }}
              />
      {/* </Box>  */}

      {/* Close Position Dialog */}
      <ClosePositionDialog
        open={closePositionDialogOpen}
        onClose={handleClosePositionDialogClose}
        positionData={selectedPositionForClose}
      />
    </Box>
  )
};
  
export default PositionsTable; 